import React, { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import supabase from '../../../services/supabase';
import { getMapboxToken } from '../../../services/mapService';
import { RouteGenerationService } from '../../../services/routeGenerationService';
import FullscreenMapButton from './FullscreenMapButton';
import RouteCollaboratorsModal from './RouteCollaboratorsModal';
import { addCollaborator, listCollaborators } from '../../../services/collaboratorService';
import './RouteCreation.css';

// Helpers to format time/cost for display
const minutesToLabel = (m) => {
  if (m == null || isNaN(m)) return null;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h > 0) return `${h}h${mm ? ` ${mm}m` : ''}`;
  return `${mm}m`;
};

const minToClock = (m) => {
  if (m == null || isNaN(m)) return null;
  const hh = Math.floor(m / 60) % 24;
  const mm = m % 60;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
};

// Progress indicator component for route viewing
const RouteViewProgressIndicator = ({ currentSection, totalSections, onSectionClick }) => {
  const sections = [
    { id: 1, label: 'Route Info', icon: '📝', sectionId: 'route-info-section' },
    { id: 2, label: 'Route Points', icon: '📍', sectionId: 'points-section' },
    { id: 3, label: 'Route Path', icon: '🛣️', sectionId: 'path-section' },
    { id: 4, label: 'Connections', icon: '🔗', sectionId: 'connections-section' }
  ];

  return (
    <div className="route-progress">
      <div className="progress-header">
        <h3>View Route</h3>
        <span className="progress-text">Section {currentSection} of {totalSections}</span>
      </div>
      <div className="progress-steps">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`progress-step ${index + 1 <= currentSection ? 'active' : ''} ${index + 1 < currentSection ? 'completed' : ''}`}
            onClick={() => {
              console.log('[DEBUG] Progress section clicked:', section.sectionId);
              onSectionClick(section.sectionId);
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="step-icon">
              {index + 1 < currentSection ? '✓' : section.icon}
            </div>
            <span className="step-label">{section.label}</span>
            {index < sections.length - 1 && <div className="step-connector" />}
          </div>
        ))}
      </div>
    </div>
  );
};

const RouteView = ({ 
  selectedRoute, 
  selectedRoutePoints, 
  loadingSelectedRoute, 
  onBack, 
  onEdit, 
  onDelete,
  mapContainerRef, 
  mapRef,
  userId,
  canEdit = true 
}) => {
  // Debug logging for map refs
  console.log('[DEBUG] RouteView component mounted with:', {
    selectedRoute: selectedRoute?.name,
    selectedRoutePoints: selectedRoutePoints?.length,
    mapContainerRef: !!mapContainerRef,
    mapRef: !!mapRef,
    canEdit,
    timestamp: new Date().toISOString()
  });

  // Refs for section navigation
  const routeInfoSectionRef = useRef(null);
  const pointsSectionRef = useRef(null);
  const pathSectionRef = useRef(null);
  const connectionsSectionRef = useRef(null);
  const markersRef = useRef([]);

  // Creator profile (for avatar in route info meta)
  const [creatorAvatarUrl, setCreatorAvatarUrl] = useState(null);
  const [creatorUsername, setCreatorUsername] = useState('');

  // State for route viewing
  const [currentSection, setCurrentSection] = useState(1);
  const [expandedSections, setExpandedSections] = useState({
    routeInfo: true,
    points: true,
    path: false,
    connections: false
  });
  
  // Collaborators state
  const [collabModalOpen, setCollabModalOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  // Route geometry and tracking state
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const [loadingRouteGeometry, setLoadingRouteGeometry] = useState(false);

  // Function to scroll to section when progress step is clicked
  const handleSectionClick = (sectionId) => {
    console.log('[DEBUG] Section clicked:', sectionId);
    
    let targetRef = null;
    
    switch (sectionId) {
      case 'route-info-section':
        targetRef = routeInfoSectionRef;
        break;
      case 'points-section':
        targetRef = pointsSectionRef;
        break;
      case 'path-section':
        targetRef = pathSectionRef;
        break;
      case 'connections-section':
        targetRef = connectionsSectionRef;
        break;
      default:
        console.log('[DEBUG] Unknown section ID:', sectionId);
        return;
    }

    console.log('[DEBUG] Target ref:', targetRef);
    console.log('[DEBUG] Target ref current:', targetRef?.current);

    if (targetRef && targetRef.current) {
      console.log('[DEBUG] Scrolling to section...');
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    } else {
      console.log('[DEBUG] Target ref or current is null');
    }
  };

  // Initialize Mapbox token once
  useEffect(() => {
    const token = getMapboxToken();
    if (token) {
      try {
        mapboxgl.accessToken = token;
      } catch (e) {
        console.error('Failed to set Mapbox token:', e);
      }
    } else {
      console.error('Mapbox token not found');
    }
  }, []);

  // Fetch creator profile image and username
  useEffect(() => {
    const fetchCreatorProfile = async () => {
      try {
        if (!selectedRoute?.user_id) return;
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', selectedRoute.user_id)
          .single();
        if (error) {
          return;
        }
        if (data) {
          const avatar = data.avatar_url || data.profilePicture || data.photo_url || null;
          setCreatorAvatarUrl(avatar);
          setCreatorUsername(data.username || data.full_name || '');
        }
      } catch (e) {
        // Non-fatal: leave placeholder
      }
    };
    fetchCreatorProfile();
  }, [selectedRoute?.user_id]);

  // Fetch collaborators
  useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        if (!selectedRoute?.id) return;
        const rows = await listCollaborators(selectedRoute.id);
        setCollaborators(rows);
      } catch (_) {}
    };
    fetchCollaborators();
  }, [selectedRoute?.id]);

  // Fetch route geometry and tracking data
  useEffect(() => {
    const fetchRouteGeometry = async () => {
      if (!selectedRoute?.id) return;
      
      try {
        setLoadingRouteGeometry(true);
        
        // First try to get from database (backend check)
        const routeData = await RouteGenerationService.getRouteDirections(selectedRoute.id);
        
        if (routeData && routeData.route_geometry) {
          console.log('[DEBUG] Found route geometry in database');
          setRouteGeometry(routeData.route_geometry);
          setRouteDistance(routeData.total_distance);
          setRouteDuration(routeData.total_duration);
          
          // Draw the route on the map
          drawRouteOnMap(routeData.route_geometry);
        } else {
          console.log('[DEBUG] No route geometry in database, generating from points');
          // Fallback: generate route from points (frontend check)
          await generateRouteFromPoints();
        }
      } catch (error) {
        console.error('[ERROR] Failed to fetch route geometry:', error);
        // Fallback: generate route from points
        await generateRouteFromPoints();
      } finally {
        setLoadingRouteGeometry(false);
      }
    };

    fetchRouteGeometry();
  }, [selectedRoute?.id, selectedRoutePoints]);

  // Generate route from points as fallback
  const generateRouteFromPoints = async () => {
    if (!selectedRoutePoints || selectedRoutePoints.length < 2) return;
    
    try {
      console.log('[DEBUG] Generating route from points as fallback');
      
      // Convert points to the format expected by RouteGenerationService
      const points = selectedRoutePoints.map(point => ({
        coordinates: point.latitude && point.longitude ? [point.longitude, point.latitude] : null
      })).filter(point => point.coordinates);
      
      if (points.length < 2) {
        console.log('[DEBUG] Not enough points with coordinates for route generation');
        return;
      }
      
      const transportMode = selectedRoute?.transport_mode || 'driving';
      const geometry = await RouteGenerationService.generateRoutePath(points, transportMode);
      
      setRouteGeometry(geometry.geometry);
      setRouteDistance(geometry.distance);
      setRouteDuration(geometry.duration);
      
      // Draw the route on the map
      drawRouteOnMap(geometry.geometry);
      
    } catch (error) {
      console.error('[ERROR] Failed to generate route from points:', error);
    }
  };

  // Draw route on the map
  const drawRouteOnMap = (geometry) => {
    if (!mapRef.current || !geometry) return;
    
    try {
      console.log('[DEBUG] Drawing route on map');
      
      // Remove existing route line if any
      if (mapRef.current.getSource('view-route')) {
        mapRef.current.removeLayer('view-route-line');
        mapRef.current.removeSource('view-route');
      }
      
      // Add the route line
      mapRef.current.addSource('view-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: geometry
        }
      });
      
      mapRef.current.addLayer({
        id: 'view-route-line',
        type: 'line',
        source: 'view-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#2b69f8',
          'line-width': 6,
          'line-opacity': 0.8
        }
      });
      
      // Fit map to route bounds
      const bounds = new mapboxgl.LngLatBounds();
      geometry.coordinates.forEach(coord => bounds.extend(coord));
      mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
      
    } catch (error) {
      console.error('Error drawing route on map:', error);
    }
  };

  // Render persistent markers for each point with coordinates
  useEffect(() => {
    if (!mapRef.current || !selectedRoutePoints) return;
    try {
      // Remove existing markers
      if (Array.isArray(markersRef.current)) {
        markersRef.current.forEach(m => {
          try { m.remove(); } catch (e) {}
        });
      }
      markersRef.current = [];
      
      // Add markers for points with coordinates
      selectedRoutePoints.forEach((point, index) => {
        if (point && point.latitude && point.longitude) {
          // Create popup with location name
          const popup = new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div style="font-family: Arial, sans-serif; padding: 8px; min-width: 150px;">
                <strong>${point.name || `Point ${index + 1}`}</strong>
                ${point.notes ? `<br><small>${point.notes}</small>` : ''}
              </div>
            `);
          
          const marker = new mapboxgl.Marker()
            .setLngLat([point.longitude, point.latitude])
            .setPopup(popup)
            .addTo(mapRef.current);
          
          markersRef.current.push(marker);
        }
      });
    } catch (e) {
      console.error('Error rendering markers:', e);
    }
  }, [selectedRoutePoints, mapRef.current]);

  // Update current section based on form state
  useEffect(() => {
    if (selectedRoutePoints && selectedRoutePoints.length > 0) {
      setCurrentSection(2);
    } else if (selectedRoute) {
      setCurrentSection(1);
    }
  }, [selectedRoute, selectedRoutePoints]);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  return (
    <div className="route-planner">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button onClick={onBack} className="cancel-btn">← Back to Routes</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" className="tab active">View</button>
          {canEdit && (
            <>
              <button onClick={onEdit} className="edit-btn">
                <span className="edit-icon">✏️</span>
                Edit
              </button>
              <button
                onClick={() => { if (typeof onDelete === 'function') onDelete(); }}
                className="delete-route-btn"
                type="button"
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="planner-content">
        {/* Progress Steps - Vertical Navbar */}
        <div className="progress-navbar">
          <RouteViewProgressIndicator 
            currentSection={currentSection} 
            totalSections={4} 
            onSectionClick={handleSectionClick}
          />
        </div>

        {/* Left Panel - Enhanced Form */}
        <div className="left-panel">
          <div className="route-form">
            {/* Route Info Section */}
            <div className="route-name-section" ref={routeInfoSectionRef}>
              <label className="route-name-label">Route Name</label>
              <input
                type="text"
                value={selectedRoute?.name || ''}
                readOnly
                className="route-name-input"
                style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
              />
              <div className="route-name-meta">
                <div className="creator-avatar" title={creatorUsername || 'Creator'}>
                  {creatorAvatarUrl ? (
                    <img src={creatorAvatarUrl} alt={creatorUsername || 'Creator'} />
                  ) : (
                    <span>{(creatorUsername || (typeof selectedRoute?.user_id === 'string' ? selectedRoute.user_id : 'U')).toString().charAt(0).toUpperCase()}</span>
                  )}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="add-collaborator-btn"
                    title="Add collaborators"
                    onClick={() => setCollabModalOpen(true)}
                  >
                    +
                  </button>
                )}
                <div className="route-privacy-badge">
                  {selectedRoute?.is_private ? '🔒 Private' : '🌐 Public'}
                </div>
              </div>

              {/* Route-level tags and cost summary */}
              {(Array.isArray(selectedRoute?.tags) && selectedRoute.tags.length > 0) && (
                <div className="selected-tags-chips" style={{ marginTop: 8 }}>
                  {selectedRoute.tags.map((tag) => (
                    <span key={tag} className="tag-btn selected" style={{ cursor: 'default' }}>{tag}</span>
                  ))}
                </div>
              )}
              {typeof selectedRoute?.cost_cents === 'number' && selectedRoute.cost_cents > 0 && (
                <div style={{ marginTop: 8, color: '#374151', fontWeight: 600 }}>
                  Total Cost: ${((selectedRoute.cost_cents || 0) / 100).toFixed(2)}
                </div>
              )}
            </div>

            {/* Route Points Section */}
            <div className="form-section" ref={pointsSectionRef}>
              <div 
                className="section-header"
              >
                <div className="section-title">
                  <span className="section-icon">📍</span>
                  <span>Route Points ({selectedRoutePoints?.length || 0})</span>
                </div>
                <div className="section-indicator">
                  {selectedRoutePoints?.length || 0}
                </div>
              </div>
              <div className={`section-content expanded`}>
                {loadingSelectedRoute ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading route points...</p>
                  </div>
                ) : selectedRoutePoints && selectedRoutePoints.length > 0 ? (
                  <div className="points-section">
                    {selectedRoutePoints.map((point, index) => (
                      <div key={point.id || index} className="point-card completed">
                        {/* Point Header */}
                        <div className="point-header">
                          <div className="point-number-badge">
                            {index + 1}
                          </div>
                          <div className="point-status">
                            <span className="status-complete">✓ Located</span>
                          </div>
                        </div>

                        {/* Point Content */}
                        <div className="point-content">
                          {/* Location Display */}
                          <div className="location-input-group">
                            <input
                              type="text"
                              value={point.location_search || 'Unknown Location'}
                              readOnly
                              className="location-input"
                              style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                            />
                          </div>

                          {/* Point Name Display */}
                          {point.name && (
                            <div className="point-name-input-group">
                              <input
                                type="text"
                                value={point.name}
                                readOnly
                                className="point-name-input"
                                style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                              />
                            </div>
                          )}

                          {/* Tags Display */}
                        {point.tags && point.tags.length > 0 && (
                          <div className="tags-row">
                            {point.tags.map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                className="tag-btn selected"
                                disabled
                                style={{ cursor: 'default' }}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        )}

                          {/* Notes Display */}
                          {point.notes && point.notes.trim() !== '' && (
                            <div className="notes-input-group">
                              <input
                                type="text"
                                value={point.notes}
                                readOnly
                                className="notes-input"
                                style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                              />
                            </div>
                          )}

                          {/* Meta: time window / duration and cost */}
                          <div className="point-meta" style={{ display: 'flex', gap: 12, marginTop: 6, color: '#374151', fontSize: 12 }}>
                            {(() => {
                              const start = typeof point.stay_start_min === 'number' ? minToClock(point.stay_start_min) : null;
                              const end = typeof point.stay_end_min === 'number' ? minToClock(point.stay_end_min) : null;
                              const duration = typeof point.stay_duration === 'number' ? minutesToLabel(point.stay_duration) : null;
                              if (start && end) return (<span>Time: <strong>{start} – {end}</strong></span>);
                              if (duration) return (<span>Time: <strong>{duration}</strong></span>);
                              return null;
                            })()}
                            {typeof point.cost_cents === 'number' && point.cost_cents > 0 && (
                              <span>Cost: <strong>${(Math.max(0, Math.round(point.cost_cents)) / 100).toFixed(2)}</strong></span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p className="input-hint">No points added to this route yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Route Path Section */}
            <div className="form-section" ref={pathSectionRef}>
              <div 
                className="section-header"
              >
                <div className="section-title">
                  <span className="section-icon">🛣️</span>
                  <span>Route Path</span>
                </div>
                <div className="section-indicator">
                  {routeGeometry ? '✓' : loadingRouteGeometry ? '⏳' : '!'}
                </div>
              </div>
              <div className={`section-content expanded`}>
                {loadingRouteGeometry ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading route path...</p>
                  </div>
                ) : (
                  <div className="transport-mode-display">
                    <p className="transport-mode-label">
                      Transport Mode: <strong>{RouteGenerationService.getTransportModeLabel(selectedRoute?.transport_mode || 'driving')} {RouteGenerationService.getTransportModeIcon(selectedRoute?.transport_mode || 'driving')}</strong>
                    </p>
                    
                    {routeDistance && (
                      <p className="route-distance">
                        Distance: <strong>{RouteGenerationService.formatDistance(routeDistance)}</strong>
                      </p>
                    )}
                    
                    {routeDuration && (
                      <p className="route-duration">
                        Duration: <strong>{RouteGenerationService.formatDuration(routeDuration)}</strong>
                      </p>
                    )}
                    
                    {selectedRoute?.created_at && (
                      <p className="route-created-date">
                        Created: {new Date(selectedRoute.created_at).toLocaleDateString()}
                      </p>
                    )}
                    
                    {!routeGeometry && selectedRoutePoints && selectedRoutePoints.length >= 2 && (
                      <p className="route-generation-note">
                        💡 Route path will be generated when you have 2+ points with coordinates
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Connections Section */}
            <div className="form-section" ref={connectionsSectionRef}>
              <div 
                className="section-header"
              >
                <div className="section-title">
                  <span className="section-icon">🔗</span>
                  <span>Route Connections</span>
                </div>
                <div className="section-indicator">
                  {collaborators?.length || 0}
                </div>
              </div>
              <div className={`section-content expanded`}>
                <div className="connections-display">
                  <p className="connections-description">
                    This route has {collaborators?.length || 0} collaborator{collaborators?.length !== 1 ? 's' : ''}.
                  </p>
                  {canEdit && (
                    <button
                      onClick={() => setCollabModalOpen(true)}
                      className="manage-collaborators-btn"
                    >
                      Manage Collaborators
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Map */}
        <div className="right-panel">
          <div className="route-creation-map" style={{ position: 'relative' }}>
            <FullscreenMapButton mapContainerRef={mapContainerRef} />
          <div 
              ref={mapContainerRef} 
              className="route-map"
            ></div>
        </div>
      </div>
      </div>

      {/* Collaborators Modal */}
      <RouteCollaboratorsModal
        open={collabModalOpen}
        onClose={() => setCollabModalOpen(false)}
        initialCollaborators={collaborators}
        onSave={(list) => { setCollaborators(list); setCollabModalOpen(false); }}
        ownerUserId={selectedRoute?.user_id}
      />
    </div>
  );
};

export default RouteView;
